/**
 * Keeps the Feedback badge and the reply pop-up current off the piggyback
 * message bus, instead of polling `/threads/unread-count`.
 *
 * The server queues a `feedback_reply` message for the submitter on every admin
 * reply (threads/addMessage.ts); the app's idle ping carries it here. Each one
 * refreshes the badge count; one flagged "Show as pop-up" also asks What's New
 * for the personal card — deferred while `hold` is set (mid-solve, or already
 * on the Feedback page) and fired once it clears.
 *
 * Requires the app to have installed the bus (installMessageBus + the ping).
 * See docs/FeatureDesigns/feedback-reply-popup-target.md.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { registerMessageHandler } from '../messages';
import { requestPersonalTipsCheck } from '../whats-new/useWhatsNew';

/** The react-query key every Feedback-badge query shares. On the student app
 *  it is the Messages badge, which counts teacher threads too. */
export const FEEDBACK_UNREAD_QUERY_KEY = ['feedback-unread-count'] as const;

export function useFeedbackReplyMessages(hold = false): void {
    const queryClient = useQueryClient();
    const holdRef = useRef(hold);
    holdRef.current = hold;
    const pending = useRef(false);

    useEffect(() => registerMessageHandler('feedback_reply', (m) => {
        queryClient.invalidateQueries({ queryKey: FEEDBACK_UNREAD_QUERY_KEY });
        if (!(m.payload as { popup?: boolean } | undefined)?.popup) return;
        if (holdRef.current) pending.current = true;
        else requestPersonalTipsCheck();
    }), [queryClient]);

    // A teacher wrote to this student (student_teacher thread): refresh the
    // Messages badge and any thread card showing that conversation.
    useEffect(() => registerMessageHandler('teacher_message', () => {
        queryClient.invalidateQueries({ queryKey: FEEDBACK_UNREAD_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ['student-teacher-thread'] });
        queryClient.invalidateQueries({ queryKey: ['help-query'] });
    }), [queryClient]);

    useEffect(() => {
        if (!hold && pending.current) {
            pending.current = false;
            requestPersonalTipsCheck();
        }
    }, [hold]);
}
